import {
  useEffect,
  useState,
  useCallback
} from 'react';
import {
  useNavigate,
  useLocation
} from 'react-router-dom';
import {
  clearCmsSession
} from '../../utils/auth';
import GeneralSettings from './GeneralSettings';
import CourseManagement from './CourseManagement';
import Assignments from './Assignments';
import {
  generateMasterToken
} from '../../utils/masterToken';
import InstructorChat from './InstructorChat';
import Applications from './Applications.js';
import Committees from './Committees';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function resolveCanonicalInstructor(
  session
) {
  const email =
    String(session?.email || '').trim();

  if (!email) {
    throw new Error(
      'The CMS session does not contain an instructor email address.'
    );
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?email=ilike.${encodeURIComponent(email)}&select=id,name,email,is_superadmin,institution_id&limit=2`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(
      `Could not verify instructor identity (${response.status})`
    );
  }

  const rows = await response.json();

  if (
    !Array.isArray(rows) ||
    rows.length !== 1
  ) {
    throw new Error(
      rows?.length > 1
        ? 'More than one instructor account uses this email address.'
        : 'No instructor account matches the logged-in email address.'
    );
  }

  const instructor = rows[0];

  return {
    ...session,
    instructorId: instructor.id,
    isSuperadmin:
      instructor.is_superadmin === true,
    email: instructor.email || email,
    instructorName:
      instructor.name || '',
    institutionId:
      instructor.institution_id || null
  };
}

export default function CmsShell({
  session,
  onLogout
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [
    effectiveSession,
    setEffectiveSession
  ] = useState(null);

  const [
    identityLoading,
    setIdentityLoading
  ] = useState(true);

  const [
    identityError,
    setIdentityError
  ] = useState('');

  const [
    isMobile,
    setIsMobile
  ] = useState(
    () =>
      typeof window !==
        'undefined' &&
      typeof window.matchMedia ===
        'function'
        ? window.matchMedia(
            '(max-width: 760px)'
          ).matches
        : false
  );

  useEffect(() => {
    if (
      typeof window ===
        'undefined' ||
      typeof window.matchMedia !==
        'function'
    ) {
      return undefined;
    }

    const query =
      window.matchMedia(
        '(max-width: 760px)'
      );

    const update = () =>
      setIsMobile(
        query.matches
      );

    update();

    if (
      typeof query.addEventListener ===
      'function'
    ) {
      query.addEventListener(
        'change',
        update
      );

      return () =>
        query.removeEventListener(
          'change',
          update
        );
    }

    query.addListener(update);

    return () =>
      query.removeListener(update);
  }, []);

  useEffect(() => {
    let cancelled = false;

    setIdentityLoading(true);
    setIdentityError('');

    resolveCanonicalInstructor(session)
      .then(resolved => {
        if (!cancelled) {
          setEffectiveSession(resolved);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setEffectiveSession(null);
          setIdentityError(
            err?.message ||
              'Could not verify the CMS instructor account.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIdentityLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const path = location.pathname
    .replace('/cms', '')
    .replace(/^\/+/, '');

  const activeTab =
    path ||
    (
      effectiveSession?.isSuperadmin
        ? 'general'
        : 'assignments'
    );

  const searchParams =
    new URLSearchParams(location.search);

  const urlCourseId =
    searchParams.get('courseId') || null;

  const [
    currentCourseId,
    setCurrentCourseId
  ] = useState(urlCourseId);

  useEffect(() => {
    if (urlCourseId) {
      setCurrentCourseId(urlCourseId);
    }
  }, [urlCourseId]);

  useEffect(() => {
    if (!effectiveSession) return;

    if (
      location.pathname === '/cms' ||
      location.pathname === '/cms/'
    ) {
      navigate(
        `/cms/${
          effectiveSession.isSuperadmin
            ? 'general'
            : 'assignments'
        }`,
        { replace: true }
      );
    }
  }, [
    location.pathname,
    navigate,
    effectiveSession
  ]);

  useEffect(() => {
    if (
      !effectiveSession ||
      effectiveSession.isSuperadmin ||
      activeTab !== 'applications'
    ) {
      return;
    }

    navigate(
      '/cms/assignments',
      { replace: true }
    );
  }, [
    activeTab,
    effectiveSession,
    navigate
  ]);

// ALL_GRADING_PARENT_NAV_RACE_FIX_V1
/*
 * Course-selection callbacks can run immediately after a child
 * component has changed the URL.
 *
 * Do not use location.search from this callback's render closure:
 * it may still contain the PREVIOUS query string. In particular,
 * All Grading may already have changed:
 *
 *   tab=all-grading
 *
 * to:
 *
 *   tab=grading
 *
 * Using the stale query here would write tab=all-grading back
 * into the URL, meaning the first click merely changes course and
 * a second click is required to open the grading panel.
 *
 * Read the browser's live URL at the moment the callback runs.
 */
const handleCourseSelected =
  useCallback(courseId => {
    const nextCourseId =
      courseId || null;

    setCurrentCourseId(
      nextCourseId
    );

    if (!nextCourseId) {
      return;
    }

    const livePathname =
      window.location.pathname;

    if (
      livePathname !==
        '/cms/courses' &&
      livePathname !==
        '/cms/assignments'
    ) {
      return;
    }

    const params =
      new URLSearchParams(
        window.location.search
      );

    if (
      params.get('courseId') ===
      nextCourseId
    ) {
      return;
    }

    params.set(
      'courseId',
      nextCourseId
    );

    navigate(
      `${livePathname}?${params.toString()}`,
      { replace: true }
    );
  }, [
    navigate
  ]);

  function handleLogout() {
    clearCmsSession();
    onLogout();
  }

  async function handleOpenStudentView() {
    if (!effectiveSession?.isSuperadmin) {
      return;
    }

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/students?is_master=eq.true&select=id&limit=1`,
      {
        headers: {
          apikey:
            process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY,
          Authorization:
            'Bearer ' +
            process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY
        }
      }
    );

    const data = await response.json();

    if (!data || !data[0]) {
      alert(
        'No master student account found. Create one in Student Management first.'
      );
      return;
    }

    const token =
      await generateMasterToken();

    const url =
      `${window.location.origin}/student?masterToken=${token}` +
      `&masterId=${data[0].id}`;

    window.open(url, '_blank');
  }

  function handleConfigure(courseId) {
    setCurrentCourseId(courseId);

    navigate(
      `/cms/courses?courseId=${encodeURIComponent(courseId)}`
    );
  }

  function handleNav(itemKey) {
    if (
      currentCourseId &&
      (
        itemKey === 'assignments' ||
        itemKey === 'courses'
      )
    ) {
      navigate(
        `/cms/${itemKey}?courseId=${encodeURIComponent(currentCourseId)}`
      );
      return;
    }

    navigate(`/cms/${itemKey}`);
  }

  if (identityLoading) {
    return (
      <div style={styles.identityState}>
        Verifying instructor access...
      </div>
    );
  }

  if (
    identityError ||
    !effectiveSession
  ) {
    return (
      <div style={styles.identityState}>
        <div style={styles.identityError}>
          {identityError ||
            'Could not verify instructor access.'}
        </div>

        <button
          style={styles.logoutBtn}
          onClick={handleLogout}
        >
          Sign Out
        </button>
      </div>
    );
  }

  const NAV_ITEMS = [
    {
      key: 'general',
      label: 'General Settings'
    },
    {
      key: 'courses',
      label: 'Course Management'
    },
    {
      key: 'assignments',
      label: 'Assignments'
    },
    {
      key: 'chat',
      label: 'Chat'
    },
    { key: 'committees', label: 'Committees' },
    ...(effectiveSession.isSuperadmin
      ? [
          {
            key: 'applications',
            label: 'Applications'
          }
        ]
      : [])
  ];

  return (
    <div style={styles.shell}>
      <header
        style={{
          ...styles.topbar,
          ...(isMobile
            ? styles.topbarMobile
            : {})
        }}
      >
        <div>
          <div style={styles.brand}>
            Student System CMS
          </div>

          <div style={styles.brandSub}>
            {effectiveSession?.email ||
              session.email}
          </div>
        </div>

        <div
          style={{
            ...styles.headerBtns,
            ...(isMobile
              ? styles.headerBtnsMobile
              : {})
          }}
        >
          <button
            style={styles.studentViewBtn}
            onClick={() =>
              window.open(
                '/voting',
                '_blank'
              )
            }
          >
            Voting
          </button>

          {effectiveSession.isSuperadmin && (
            <button
              style={styles.studentViewBtn}
              onClick={
                handleOpenStudentView
              }
            >
              Student View
            </button>
          )}

          <button
            style={styles.logoutBtn}
            onClick={handleLogout}
          >
            Sign Out
          </button>
        </div>
      </header>

      <div
        style={{
          ...styles.layout,
          ...(isMobile
            ? styles.layoutMobile
            : {})
        }}
      >
        <nav
          style={{
            ...styles.nav,
            ...(isMobile
              ? styles.navMobile
              : {})
          }}
        >
          {NAV_ITEMS.map(item => (
            <button
              key={item.key}
              style={{
                ...styles.navBtn,
                ...(isMobile
                  ? styles.navBtnMobile
                  : {}),
                ...(activeTab ===
                item.key
                  ? styles.navBtnActive
                  : {})
              }}
              onClick={() =>
                handleNav(item.key)
              }
            >
              {item.label}
            </button>
          ))}
        </nav>

        <main
          style={{
            ...styles.content,
            ...(isMobile
              ? styles.contentMobile
              : {})
          }}
        >
          {activeTab === 'committees' && (
  <Committees session={effectiveSession} />
)}
          {activeTab === 'general' && (
            <GeneralSettings
              session={effectiveSession}
              onConfigure={
                handleConfigure
              }
            />
          )}

          {activeTab === 'courses' && (
            <CourseManagement
              session={effectiveSession}
              initialCourseId={
                currentCourseId
              }
              onCourseSelected={
                handleCourseSelected
              }
            />
          )}

          {activeTab ===
            'assignments' && (
            <Assignments
  session={effectiveSession}
  initialCourseId={
    currentCourseId
  }
  onCourseSelected={
    handleCourseSelected
  }
/>
          )}

          {activeTab === 'chat' && (
            <InstructorChat
              session={effectiveSession}
            />
          )}

          {activeTab ===
            'applications' &&
            effectiveSession.isSuperadmin && (
              <Applications
                session={effectiveSession}
              />
            )}
        </main>
      </div>
    </div>
  );
}

const styles = {
  identityState: {
    minHeight: '100vh',
    background: '#f4f6f8',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '14px',
    color: '#42515f',
    fontSize: '14px'
  },
  identityError: {
    maxWidth: '520px',
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    color: '#a12a2a',
    borderRadius: '8px',
    padding: '12px 16px',
    textAlign: 'center'
  },
  shell: {
    minHeight: '100vh',
    background: '#f4f6f8'
  },
  topbar: {
    background: 'white',
    borderBottom:
      '1px solid #d7dee5',
    padding: '14px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 20
  },
  topbarMobile: {
    padding: '10px 12px',
    gap: '10px',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  brand: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2933'
  },
  brandSub: {
    color: '#66788a',
    fontSize: '13px',
    marginTop: '2px'
  },
  logoutBtn: {
    border: '1px solid #d7dee5',
    background: 'white',
    borderRadius: '8px',
    padding: '7px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  layout: {
    display: 'grid',
    gridTemplateColumns:
      '220px minmax(0, 1fr)',
    minHeight:
      'calc(100vh - 67px)',
    width: '100%'
  },
  layoutMobile: {
    display: 'block',
    minHeight: 0
  },
  nav: {
    background: '#233746',
    padding: '18px 12px'
  },
  navMobile: {
    display: 'flex',
    gap: '6px',
    padding: '8px',
    overflowX: 'auto',
    WebkitOverflowScrolling:
      'touch'
  },
  navBtn: {
    width: '100%',
    textAlign: 'left',
    border: 0,
    background: 'transparent',
    color: '#dce7ef',
    borderRadius: '8px',
    padding: '11px 12px',
    marginBottom: '5px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '14px'
  },
  navBtnActive: {
    background: '#36546a',
    color: 'white'
  },
  navBtnMobile: {
    width: 'auto',
    flex: '0 0 auto',
    marginBottom: 0,
    padding: '9px 11px',
    whiteSpace: 'nowrap'
  },
  content: {
    padding: '24px',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  contentMobile: {
    padding: '10px'
  },
  headerBtns: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  headerBtnsMobile: {
    width: '100%',
    overflowX: 'auto',
    flexWrap: 'nowrap',
    WebkitOverflowScrolling:
      'touch'
  },
  studentViewBtn: {
    border: '1px solid #b0cfe0',
    background: '#e7f1f7',
    borderRadius: '8px',
    padding: '7px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#215b7d'
  }
};